import axios from "axios";
import * as Sentry from "@sentry/nextjs";
import { format, parseISO, addMinutes, isBefore } from "date-fns";
import { InsuranceData } from "@/types/insurance.type";
import { CalendarById } from "@/types/calendar";
import { formatInTimeZone } from "date-fns-tz";
// ========================================================================
export type CancellationPayload = {
	meetingId: string;
	eventId: string;
	googleCalendarId: string;
};

export type APIResponse<T> = {
	data: T;
	status: number;
	isLoading: boolean;
	isError: boolean;
};

export type Info = {
	fullName: string;
	email: string;
	phone: string;
	address: string;
	gender: string;
	city: string;
	state: string;
	zip: string;
	dpc?: boolean;
	promo?: string;
	couponCode?: string;
	weightLoss?: boolean;
};

export type Payment = {
	cardNumber: string;
	transactionNo: string;
	cardHolderName: string;
	submitted?: boolean;
};

export type Booking = {
	id?: number | string;
	date: string | Date;
	serviceId?: string;
	staffId?: string;
	timeZone: string;
	repick: boolean;
	booked?: boolean;
};

export type Patient = {
	phone: string;
	first_name: string;
	last_name: string;
	email: string;
	street: string;
	city: string;
	state: string;
	country: string;
	zip: string;
	gender: string;
	promo: string;
	couponCode: string;
	selected_state?: string;
	dob?: string;
};

export type ServicePayload = Patient & {
	service_type: string;
	Bookings_Staff_ID: string;
	Start_time: string | Date;
	End_time: string | Date;
	patientId: string | null;
	patientId_long?: string | null;
	item_id: string | null;
	eventId: string | null;
	duration: number;
	channel: string;
	meeting_status: "upcoming" | "Reschedule";
	CRM_Meeting_ID: string | null;
	patient_timezone: string;
	patient_time: string | Date;
	payment_skipped: boolean;
	country: string;
	insurance?: string;
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_content?: string;
	utm_keyword?: string;
	utm_term?: string;
	current?: {
		eventId: string;
		calendarId: string;
		meetingId: string;
	};
	googleCalendarId: string;
	additionalQuestions?: Answer[];
	serviceId: string;
	staffId: string;
	oldCalendarId?: string;
	org?: string;
	block_no_copay?: boolean;
};

export type Answer = {
	question: string;
	value: string;
};

export type ServiceCommon = ServicePayload & {
	buffer: number;
	cost: number;
	booked?: boolean;
	new?: boolean;
	preloaded?: boolean;
	couponCode?: string;
	fromSA?: boolean;
	selected_state?: string;
	oldEmail?: string;
	oldPhone?: string;
	_ef_transaction_id?: string;
	flow_type?: string;
};

export type StaffAvailability = {
	staff?: string[];
	serviceId?: string;
	start: Date;
	end: Date;
	slots?: AvailableSlots[];
};

export type AvailableSlots = {
	staff: string;
	date: string;
	serviceId: string;
	timeSlots: string[];
	timeZone: string;
};

export type AvailableSlotsNew = {
	id?: string[];
	link?: string[];
	staffId: string;
	staffEmail: string;
	date: string;
	takenSlots: string[];
	available: string[];
	timezone: string;
};

export type WorkSpace = {
	Name: string;
	id: string;
};

export type ServiceType = {
	Profile_Picture: string | null;
	service_id: string;
	service_name: string;
	staff_email: string;
	staff_id: string;
	staff_name: string;
	staff_bio: string | null;
	state: string | null;
	blockedDays: string[];
	Maximum_Booking_Notice_Min: number | null;
	Minimum_Booking_Notice_Min: number | null;
	service_provider_id?: string | null;
	title: string;
	Block_Free_Appointments?: boolean;
};

export type RequiredDocument = {
	document_name: string;
	base_url: string;
};

export type AdditionalQuestion = {
	Question: string;
	Type: string;
	required: string | boolean;
	Options: string | null;
};

export type ServiceInfoType = {
	Additional_Questions: AdditionalQuestion[][] | null;
	Buffer_Time_Min: number;
	Cost: number;
	Cancelation_and_Rescheduling_Window_Min: number;
	Channel: string;
	Duration_Min: number;
	Maximum_Booking_Notice_Min: number;
	Minimum_Booking_Notice_Min: number;
	Scheduling_Interval_Min: number;
	Insurance_Required: boolean;
	Status: string;
	cancelation_cost: number;
	Visibility: string;
	service_name: string;
	State_Required: boolean;
	required_documents: RequiredDocument[][] | null;
	Books_Item_ID: string;
	capture_card?: boolean;
	org?: string;
	includedMins?: string[];
};

export type DPCType = {
	Staff_Data_Name: string;
	Name: string;
	Email: string;
	Agent_ID: string;
	Bookings_Staff_ID: string;
	Role: string;
	Assigned_PCC?: any;
	Meeting_Staff_ID?: any;
	Subjective_Female?: any;
	Assessment_Female?: any;
	Objective_Female?: any;
	Plan_Female?: any;
	Subjective_Male?: any;
	Assessment_Male?: any;
	Objective_Male?: any;
	Plan_Male?: any;
	userPreference: UserPreference;
};

interface UserPreference {
	MinBookingNoticeMinutes: number;
	SMSBookingNotification: string;
	EmailBookingNotification: string;
	MeetingBufferMinutes: number;
}

export type Consent = {
	signature: string;
};

export type IdTypes = "GovId" | "Passport" | "Military";

export type DriverLicense = {
	driverLicense: string;
	driverLicenseFront?: string;
	type: IdTypes;
	file?: File | Blob;
};

export type Field =
	| "email"
	| "completeDate"
	| "gender"
	| "maritalStatus"
	| "previouslyTriedHormoneTherapy"
	| "oftenExercise"
	| "dietsCurrentlyOn"
	| "stressAtHome"
	| "stressLevel"
	| "currentMeds"
	| "general"
	| "neurological"
	| "musculoskeletal"
	| "endocrine"
	| "liverDisease"
	| "cardiovascular"
	| "respiratory"
	| "gastrointestinal"
	| "genitourinary"
	| "psychiatric"
	| "autoImmune"
	| "type"
	| "when"
	| "status"
	| "surgicalProcedures"
	| "lastColonoscopySigmoidoscopy"
	| "lastCardiacStress"
	| "lastDigitalRectal"
	| "lastPsaProstate"
	| "driverLicense"
	| "desireChildren"
	| "howSoon"
	| "lastBoneDensity"
	| "lastMammogram"
	| "lastMenstrual"
	| "lastPapSmear"
	| "lastPelvicUltrasound"
	| "birthControl"
	| "noPregnancies"
	| "noChildren"
	| "allergies"
	| "abnormalPap"
	| "provideDate"
	| "noMiscarriages"
	| "iudType"
	| "noAbortions"
	| "otp"
	| "id"
	| "user"
	| "rawImage"
	| "insurerName"
	| "policyHolderName"
	| "policyNumber"
	| "groupNumber"
	| "weightLoss"
	| "primaryInsurance"
	| "secondaryInsurance"
	| "hasInsurance";

type User = {
	user?: any;
};

export type IntakeFields = User & {
	email?: string;
	completeDate?: string;
	gender?: string;
	maritalStatus?: string;
	Do_you_have_insurance?: string;
	previouslyTriedHormoneTherapy?: string;
	oftenExercise?: string;
	dietsCurrentlyOn?: string;
	stressAtHome?: string;
	stressLevel?: string;
	currentMeds?: any;
	general?: string[];
	neurological?: string[];
	musculoskeletal?: string[];
	endocrine?: string[];
	liverDisease?: string[];
	cardiovascular?: string[];
	respiratory?: string[];
	gastrointestinal?: string[];
	genitourinary?: string[];
	psychiatric?: string[];
	autoImmune?: string[];
	type?: string;
	when?: string;
	status?: string;
	surgicalProcedures?: any[];
	lastColonoscopySigmoidoscopy?: string;
	lastCardiacStress?: string;
	lastDigitalRectal?: string;
	lastPsaProstate?: string;
	driverLicense?: DriverLicense;
	desireChildren?: string;
	howSoon?: string;
	lastBoneDensity?: string;
	lastMammogram?: string;
	lastMenstrual?: string;
	lastPapSmear?: string;
	lastPelvicUltrasound?: string;
	birthControl?: string;
	noPregnancies?: string;
	noChildren?: string;
	allergies?: string;
	abnormalPap?: string;
	provideDate?: string;
	noMiscarriages?: string;
	iudType?: string;
	noAbortions?: string;
	otp?: string;
	id?: string;
	user?: any;
	rawImage?: string;
	insurerName?: string;
	policyHolderName?: string;
	policyNumber?: string;
	groupNumber?: string;
	primaryInsurance?: InsuranceData;
	secondaryInsurance?: InsuranceData;
	weightLoss?: boolean;
	hasInsurance?: boolean;
	cash?: boolean;
	submitting: boolean;
};

export type BookingType = {
	data: {
		info: Info;
		payment: Payment;
		consent: Consent;
		booking: Booking;
		freeConsult: ServiceCommon;
		search: SearchParams;
		intake?: IntakeFields;
		payors?: string[];
		additionalQuestions: AdditionalQuestion[][] | null;
		requiredDocuments: RequiredDocument[][] | null;
		meetingDetails: CalendarById | null;
		services: ServiceType[] | null;
		serviceInfo: ServiceInfoType | null;
		entryPoint?: string;
		utm_campaign?: string;
		utm_term?: string;
		utm_content?: string;
		utm_keyword?: string;
	};
};

export type InvoiceType = {
	email: string;
	invoiceId: string;
	authTransId?: string;
	authRefId?: string;
	firstName: string;
	lastName: string;
	phone: string;
	cc: string;
	ex: string;
	cvv: string;
	cost: number;
	source: string;
	org: string;
	serviceId: string;
};

export type OptionType = { label: string; value: "" };

export type SubQuestion = QuestionType & { trigger?: string[] };

export type Available = {
	timeZone: string;
	date: string;
	time: string;
	orgTime: string;
	id?: string[];
	link?: string[];
	staffId: string;
	staffEmail: string;
	hour: number;
};

export type QuestionType = {
	id?: number;
	name: string;
	label: string;
	note?: string;
	type: string;
	options?: OptionType[];
	withSubmit?: boolean;
	subTitle?: string;
	required?: boolean;
	OtpTrigger?: boolean;
	placeHolder?: string;
	submitCaption?: string;
	skippable?: boolean;
	min?: number;
	max?: number;
	sub?: SubQuestion[];
	questions?: [QuestionType];
	minYears?: number;
	allowMo?: boolean;
	hideProgress?: boolean;
};

export type SearchParams = {
	staff: string[];
	serviceId: string;
	start: Date;
	end: Date;
	state: string;
};

export type BookingContextType = BookingType & {
	// eslint-disable-next-line no-unused-vars
	setInfo: (values: Info) => void;
	// eslint-disable-next-line no-unused-vars
	setPayment: (values: Payment) => void;
	// eslint-disable-next-line no-unused-vars
	setConsent: (values: Consent) => void;
	// eslint-disable-next-line no-unused-vars
	setBooking: (values: Booking) => void;
	// eslint-disable-next-line no-unused-vars
	setParams: (values: SearchParams) => void;
	// eslint-disable-next-line no-unused-vars
	setIntake: (values: IntakeFields) => void;
	// eslint-disable-next-line no-unused-vars
	setFreeConsult: (values: ServiceCommon) => void;
	// eslint-disable-next-line no-unused-vars
	reset: (data?: BookingType) => void;
	// eslint-disable-next-line no-unused-vars
	setAdditionalQuestions: (data: [AdditionalQuestion[]] | null) => void;
	// eslint-disable-next-line no-unused-vars
	setRequiredDocs: (data: [RequiredDocument[]] | null) => void;
	// eslint-disable-next-line no-unused-vars
	setMeetingDetail: (data: CalendarById) => void;

	// eslint-disable-next-line no-unused-vars
	setServices: (data: ServiceType[]) => void;

	// eslint-disable-next-line no-unused-vars
	setPayors: (payors: string[]) => void;
};

export const bookingInfo: BookingType = {
	data: {
		info: {
			fullName: "",
			gender: "",
			email: "",
			phone: "",
			address: "",
			city: "",
			state: "",
			zip: "",
			dpc: false,
		},
		serviceInfo: null,
		payment: {
			cardNumber: "",
			transactionNo: "",
			cardHolderName: "",
		},
		consent: { signature: "" },
		booking: {
			staffId: "",
			serviceId: "",
			date: "",
			timeZone: "",
			repick: false,
			booked: false,
		},
		search: {
			staff: [],
			serviceId: "",
			start: new Date(),
			end: new Date(),
			state: "",
		},
		freeConsult: {
			service_type: "",
			Bookings_Staff_ID: "",
			Start_time: "",
			End_time: "",
			patientId: null,
			eventId: null,
			duration: 0,
			channel: "",
			phone: "",
			first_name: "",
			last_name: "",
			email: "",
			payment_skipped: false,
			meeting_status: "upcoming",
			CRM_Meeting_ID: null,
			patient_timezone: "",
			patient_time: "",
			street: "",
			city: "",
			state: "",
			country: "USA",
			zip: "",
			gender: "",
			utm_source: undefined,
			utm_medium: undefined,
			utm_campaign: undefined,
			utm_content: undefined,
			utm_keyword: undefined,
			current: {
				eventId: "",
				calendarId: "",
				meetingId: "",
			},
			googleCalendarId: "",
			additionalQuestions: [],
			serviceId: "",
			staffId: "",
			buffer: 0,
			cost: 0,
			booked: false,
			new: true,
			preloaded: false,
			item_id: "",
			promo: "",
			couponCode: "",
			selected_state: "",
		},
		additionalQuestions: null,
		requiredDocuments: null,
		meetingDetails: null,
		services: null,
	},
};

const validateResponse = async (response: any) => {
	const data = response?.data?.message;
	const booked =
		JSON.parse(data.output)?.response !== "Selected slots is booked already";

	return booked;
};

export const submitBooking = async (
	context: BookingContextType,
	booking: Booking,
	// eslint-disable-next-line no-unused-vars
	onSuccess: (booking: Booking) => void | Promise<boolean> | any,
	onFailure: () => void | Promise<boolean> | any,
) => {
	try {
		const { info } = context.data;

		Sentry.addBreadcrumb({
			category: "booking",
			message: "Booking submission started",
			level: "info",
			data: {
				email: info.email,
				serviceId: booking.serviceId,
				staffId: booking.staffId,
				bookingDate: format(new Date(booking.date), "yyyy-MM-dd H:mm"),
			},
		});

		const result = await axios.post("/api/book-appointment", {
			id: booking.id,
			fullName: info.fullName,
			email: info.email,
			timezone: booking.timeZone,
			address: info.address,
			slotDateTime: format(new Date(booking.date), "yyyy-MM-dd H:mm"),
			bookSlots: format(new Date(booking.date), "H:mm"),
			bookDate: format(new Date(booking.date), "yyyy-MM-dd"),
			staffId: booking.staffId,
			serviceId: booking.serviceId,
			phone: info.phone,
		});

		const booked = await validateResponse(result);

		if (booked && onSuccess) {
			Sentry.addBreadcrumb({
				category: "booking",
				message: "Booking submission successful",
				level: "info",
				data: {
					email: info.email,
					serviceId: booking.serviceId,
				},
			});
			onSuccess(booking);
		} else {
			Sentry.captureMessage("Booking failed: Slot already booked", {
				level: "warning",
				tags: {
					function: "submitBooking",
					reason: "slot_unavailable",
				},
				extra: {
					email: info.email,
					serviceId: booking.serviceId,
					staffId: booking.staffId,
					bookingDate: format(new Date(booking.date), "yyyy-MM-dd H:mm"),
				},
			});
			onFailure();
		}
	} catch (error) {
		Sentry.captureException(error, {
			tags: {
				function: "submitBooking",
			},
			extra: {
				email: context.data.info.email,
				serviceId: booking.serviceId,
				staffId: booking.staffId,
			},
		});
		onFailure();
	}
};

type AvailabilityCheck = {
	staffId: string;
	email: string;
	duration: number;
	buffer: number;
	start: string;
	timeZone: string;
	selectedSlot?: {
		time: string;
		minBooking?: number;
	};
};

export const stillAvailable = async ({
	staffId,
	email,
	duration,
	buffer,
	start,
	timeZone,
	selectedSlot,
}: AvailabilityCheck) => {
	if (selectedSlot) {
		if (
			isBefore(
				new Date(selectedSlot.time),
				addMinutes(new Date(), Number(selectedSlot.minBooking)),
			)
		) {
			return false;
		}
	}
	const payload = {
		staff: [
			{
				id: staffId,
				blockedDays: [],
				email,
			},
		],

		interval: duration,
		buffer,
		startDate: formatInTimeZone(start, timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
		endDate: formatInTimeZone(
			addMinutes(parseISO(start), duration),
			timeZone,
			"yyyy-MM-dd HH:mm:ssXXX",
		),
		timeZone,
	};

	const res = await axios.post(`${process.env.TL_API}/calendar`, payload);
	const { data } = res;

	return (
		data?.length > 0 &&
		data[0]?.available?.includes(
			formatInTimeZone(start, timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
		)
	);
};

export const parkSlot = async (
	staffEmail: string,
	staffId: string,
	slot: string,
	ipAddress: string,
	interval: number,
	timeZone: string,
) => {
	const res = await axios.post(`/api/park-slot`, {
		staffEmail,
		staffId,
		slot,
		ipAddress,
		interval,
		timeZone,
	});
	const { data } = res;
	return data;
};

export const sendEverflowPostback = async (
	eventId?: string,
	transactionId?: string,
	email?: string,
	amount?: string | number,
	aid?: string,
	adv_event_id?: string
) => {
	try {
		Sentry.addBreadcrumb({
			category: "tracking",
			message: "Everflow postback started",
			level: "info",
			data: {
				eventId,
				transactionId,
				email,
				amount,
			},
		});

		const response = await fetch(`${process.env.TL_API}/tracking/everflow`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ eventId, transactionId, email, amount, aid, adv_event_id }),
		});

		const result = await response.json();

		if (!response.ok) {
			Sentry.captureMessage("Everflow postback failed", {
				level: "warning",
				tags: {
					function: "sendEverflowPostback",
					status: response.status.toString(),
				},
				extra: {
					eventId,
					transactionId,
					email,
					amount,
					responseStatus: response.status,
					responseData: result,
				},
			});
		} else {
			Sentry.addBreadcrumb({
				category: "tracking",
				message: "Everflow postback successful",
				level: "info",
				data: {
					eventId,
					email,
				},
			});
		}
	} catch (error) {
		Sentry.captureException(error, {
			tags: {
				function: "sendEverflowPostback",
			},
			extra: {
				eventId,
				transactionId,
				email,
				amount,
			},
		});
	}
};
